import { z } from 'zod';
import { VISIBILITY_VALUES } from '../constants.js';
import { BooleanQuerySchema, PaginationQuerySchema } from './common.js';
import { CANONICAL_SESSION_KEY_REGEX } from './introduction.js';
// ─── Session references (FR #299 / CTSK-146) ────────────────────────
//
// A project carries a list of "session references" — strings pointing
// at a Cowork session, a Slack thread, or any external conversation /
// log that is the context for the project. Stored as JSONB on
// `projects.session_refs` (migration 0040). Structured records, not
// string[] — provenance (`added_at`, `added_by_source_tag`) can't be
// retrofitted onto opaque strings already in the field.
//
// NO `kind` enum is stored. Kind is derived at render/validation time
// by pattern (classifyProjectSessionRef below), so new ref types need
// zero schema churn.
/** Hard cap on a single ref string. */
export const PROJECT_SESSION_REF_MAX_LENGTH = 512;
/** Hard cap on the list length per project. */
export const PROJECT_SESSION_REFS_MAX_COUNT = 50;
/** Optional human label cap. */
export const PROJECT_SESSION_REF_LABEL_MAX_LENGTH = 200;
/** True when the ref *claims* to be a session_key (prefix sniff). A
 *  shaped-but-ungrammatical ref is a validation error, not an opaque
 *  ref — otherwise a typo'd session_key would silently degrade to
 *  display-only. */
export function isSessionKeyShaped(ref) {
    return ref.startsWith('local_') || ref.startsWith('cse_');
}
/** Basic Slack message/thread URL pattern (CTSK-146 Q4: "basic
 *  pattern", warn-don't-block posture — this is deliberately loose). */
export const SLACK_REF_REGEX = /^https:\/\/[a-z0-9][a-z0-9-]*\.slack\.com\/(archives|messages)\/\S+$/i;
/** True when the ref *claims* to be a Slack URL (host sniff). */
export function isSlackShaped(ref) {
    return /^https?:\/\/([a-z0-9][a-z0-9-]*\.)?slack\.com([/?#]|$)/i.test(ref);
}
/**
 * Derive the render-time kind of a session ref.
 *  - CTSK-208 canonical session_key → 'session_key' (link to greet
 *    intro doc when resolvable)
 *  - Slack message URL → 'slack' (link out)
 *  - any other http(s) URL → 'url' (link out)
 *  - everything else → 'opaque' (display + copy-to-clipboard)
 */
export function classifyProjectSessionRef(ref) {
    if (CANONICAL_SESSION_KEY_REGEX.test(ref))
        return 'session_key';
    if (SLACK_REF_REGEX.test(ref))
        return 'slack';
    // Regex-only URL sniff — shared builds without the DOM lib, so no
    // `new URL()` here. Loose on purpose: an http(s)-looking string that
    // fails to parse in a browser just renders as a dead link-out.
    if (/^https?:\/\/[^\s/$.?#][^\s]*$/i.test(ref))
        return 'url';
    return 'opaque';
}
/**
 * Validate a list of ref strings (shape rules only — attestation is
 * the server's job). Returns [] when clean. Pure; reused by the API
 * routes and unit-testable without a DB.
 */
export function validateProjectSessionRefs(refs) {
    const issues = [];
    if (refs.length > PROJECT_SESSION_REFS_MAX_COUNT) {
        issues.push({ index: PROJECT_SESSION_REFS_MAX_COUNT, issue: 'too_many_refs' });
    }
    const seen = new Set();
    refs.forEach((ref, index) => {
        if (ref.length === 0) {
            issues.push({ index, issue: 'ref_empty' });
            return;
        }
        if (ref.length > PROJECT_SESSION_REF_MAX_LENGTH) {
            issues.push({ index, issue: 'ref_too_long' });
            return;
        }
        if (isSessionKeyShaped(ref) && !CANONICAL_SESSION_KEY_REGEX.test(ref)) {
            issues.push({ index, issue: 'invalid_session_key_ref' });
            return;
        }
        if (isSlackShaped(ref) && !SLACK_REF_REGEX.test(ref)) {
            issues.push({ index, issue: 'invalid_slack_ref' });
            return;
        }
        if (seen.has(ref)) {
            issues.push({ index, issue: 'duplicate_ref' });
            return;
        }
        seen.add(ref);
    });
    return issues;
}
/** Full stored record — what GET returns and what the DB holds. Wire
 *  keys are snake_case per the ratified design comment (the record is
 *  a persisted document shape, not a route param). */
export const ProjectSessionRefSchema = z.object({
    ref: z.string().trim().min(1).max(PROJECT_SESSION_REF_MAX_LENGTH),
    label: z.string().trim().min(1).max(PROJECT_SESSION_REF_LABEL_MAX_LENGTH).optional(),
    added_at: z.string().datetime({ offset: true }),
    added_by_source_tag: z.string().min(1).max(64),
});
/** PATCH-replace input record: provenance fields optional — the server
 *  preserves `added_at`/`added_by_source_tag` for refs that already
 *  exist on the project and stamps new ones itself. Clients cannot
 *  forge provenance on refs they didn't add. */
export const ProjectSessionRefInputSchema = z.object({
    ref: z.string().trim().min(1).max(PROJECT_SESSION_REF_MAX_LENGTH),
    label: z.string().trim().min(1).max(PROJECT_SESSION_REF_LABEL_MAX_LENGTH).optional(),
    added_at: z.string().datetime({ offset: true }).optional(),
    added_by_source_tag: z.string().min(1).max(64).optional(),
});
/** POST /projects/:id/session-refs body (append-only producer path). */
export const ProjectSessionRefAppendSchema = z.object({
    ref: z.string().trim().min(1).max(PROJECT_SESSION_REF_MAX_LENGTH),
    label: z.string().trim().min(1).max(PROJECT_SESSION_REF_LABEL_MAX_LENGTH).optional(),
});
// ─── Multi-source affordance (CTSK-363 Slice A) ─────────────────────
//
// Prior to this, `sourceUrl` was the only place to put a project's
// external link, and operators wanting more than one (e.g. a repo +
// a docs site) resorted to jamming a comma-separated list into that
// single text field. `new URL()` doesn't throw on a comma-joined
// string (the comma just becomes part of the path), so the value
// round-tripped through validation — but the "open" affordance then
// linked to one nonsense combined URL instead of either real one.
//
// `sources[]` replaces that workaround with a real array of
// {url, label?} records. `sourceUrl` stays as a derived compat field
// for one release (dual-written server-side from
// `sources[defaultSourceIndex]`) — see rowToProject / the PATCH route
// in apps/api/src/routes/projects.ts. Migration 0046 backfills
// existing comma-separated `source_url` values into `sources[]`.
/** Hard cap on the sources list length per project. */
export const PROJECT_SOURCES_MAX_COUNT = 20;
/** Optional human label cap (mirrors PROJECT_SESSION_REF_LABEL_MAX_LENGTH). */
export const PROJECT_SOURCE_LABEL_MAX_LENGTH = 200;
/** One source record — url + optional label. No provenance (unlike
 *  session refs): sources are operator-authored, not self-attested by
 *  producer sessions, so there's no writer identity to preserve. */
export const ProjectSourceSchema = z.object({
    url: z.string().trim().max(2048).url(),
    label: z.string().trim().min(1).max(PROJECT_SOURCE_LABEL_MAX_LENGTH).optional(),
});
/**
 * Validate a list of source records (shape rules only — zod already
 * enforces per-record url/label shape via ProjectSourceSchema; this
 * catches cross-record issues zod can't express). Returns [] when
 * clean. Pure; reused by the API route and unit-testable without a DB.
 */
export function validateProjectSources(sources) {
    const issues = [];
    if (sources.length > PROJECT_SOURCES_MAX_COUNT) {
        issues.push({ index: PROJECT_SOURCES_MAX_COUNT, issue: 'too_many_sources' });
    }
    const seen = new Set();
    sources.forEach((s, index) => {
        if (seen.has(s.url)) {
            issues.push({ index, issue: 'duplicate_url' });
            return;
        }
        seen.add(s.url);
    });
    return issues;
}
// ─── Per-project default filter (CTSK-363 Slice B) ──────────────────
//
// A project can store a default filter for its work-item list, shaped
// as a raw URL query string (e.g. "status=in_progress&label=preview-
// blocker"). Applied by the web app (apps/web/src/pages/WorkItemsList
// .tsx) when the list is opened scoped to this project
// (?projectId=<id>) and carries no OTHER explicit query params — an
// explicit/shared URL always wins over the saved default.
//
// Note: the FR's "project detail workitems tab" doesn't exist as a
// literal tab — ProjectDetail.tsx is read-only and links out to
// /workitems?projectId=<id>, which is where the real (already
// URL-driven) filter state lives. The default hydrates there.
export const PROJECT_DEFAULT_FILTER_QUERY_MAX_LENGTH = 2000;
/** Raw query-string value (no leading `?`, not a full URL — those are
 *  shape mistakes a client-side form control shouldn't be able to
 *  round-trip into the DB). Empty string clears the stored default. */
export const ProjectDefaultFilterQuerySchema = z.union([
    z.literal('').transform(() => null),
    z
        .string()
        .trim()
        .max(PROJECT_DEFAULT_FILTER_QUERY_MAX_LENGTH)
        .refine((v) => !v.startsWith('?'), 'must not include a leading "?"')
        .refine((v) => !v.includes('://'), 'must be a query string, not a full URL'),
    z.null(),
]);
/**
 * Strict (CTSK-445) — unknown keys are rejected with 400
 * VALIDATION_ERROR naming the offending key, instead of Zod's default
 * silent strip. Class-fix for the CTSK-427 silent-drop bug (memory
 * feedback_zod_field_name_silent_drop).
 */
export const ProjectCreateSchema = z.object({
    key: z.string().min(2).max(16).regex(/^[A-Z][A-Z0-9]+$/, 'uppercase letters/digits, must start with letter'),
    name: z.string().min(1).max(200),
    description: z.string().max(2000).nullable().optional(),
    defaultWorkflowId: z.string().optional(),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().optional(),
    /**
     * External repository / source URL — e.g., a GitHub repo. Named
     * generically so a GitLab / Bitbucket / Codeberg / doc-site URL is
     * equally valid. Validated as a URL when present; empty string is
     * coerced to null so the edit-form "clear to empty" UX is simple.
     * See migration 0027.
     */
    sourceUrl: z
        .union([
        z.literal('').transform(() => null),
        z.string().trim().max(2048).url(),
        z.null(),
    ])
        .optional(),
    /**
     * Cross-org visibility. Defaults to 'private' (the DB default). Setting
     * 'public' on create requires `projects:share` — enforced at the route
     * layer. See VISIBILITY_VALUES in constants.ts.
     */
    visibility: z.enum(VISIBILITY_VALUES).optional(),
}).strict();
// .strip() (at the end of the .extend() below) restores Zod's default
// unknown-key tolerance for PATCH — CTSK-445 made Create strict, and
// .partial()/.extend() would otherwise inherit `unknownKeys: "strict"`.
// Patch semantics deliberately unchanged.
export const ProjectPatchSchema = ProjectCreateSchema.partial().extend({
    isArchived: z.boolean().optional(),
    /**
     * Session references — full-array REPLACE, operator-only (PAT
     * writers get 403 at the route layer; producers use the append-only
     * POST /projects/:id/session-refs instead). Deliberately absent from
     * ProjectCreateSchema: refs accrete on live projects, they aren't
     * part of project creation. FR #299 / CTSK-146.
     */
    sessionRefs: z
        .array(ProjectSessionRefInputSchema)
        .max(PROJECT_SESSION_REFS_MAX_COUNT)
        .optional(),
    /**
     * Multi-source affordance (CTSK-363 Slice A) — full-array replace,
     * same cap-gating as the rest of PATCH /projects/:id (workitems:edit,
     * no operator-only restriction: unlike sessionRefs there's no
     * self-attested provenance to protect). Route derives + dual-writes
     * `sourceUrl` from `sources[defaultSourceIndex]`.
     */
    sources: z.array(ProjectSourceSchema).max(PROJECT_SOURCES_MAX_COUNT).optional(),
    /** Index into `sources[]` designating the row-inline default. Route
     *  clamps to a valid index if it's out of range for the resolved
     *  sources array (defensive — a client sending a stale index
     *  shouldn't be able to make the project unrenderable). */
    defaultSourceIndex: z.number().int().min(0).optional(),
    /** Per-project default filter (CTSK-363 Slice B). '' clears it. */
    defaultFilterQuery: ProjectDefaultFilterQuerySchema.optional(),
    /**
     * Ownership transfer (CTSK-449). Cap-gated at the route layer
     * (`projects:transfer_ownership`); target validated as an active
     * member of the project's org. `null` unassigns the owner.
     * Deliberately absent from ProjectCreateSchema — POST /projects
     * stamps ownerId from the session (CTSK-446).
     */
    ownerId: z.string().length(26).nullable().optional(),
}).strip();
export const ProjectListQuerySchema = PaginationQuerySchema.extend({
    q: z.string().optional(),
    includeArchived: BooleanQuerySchema.optional(),
});
//# sourceMappingURL=project.js.map